import * as am5 from '@amcharts/amcharts5'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import * as am5xy from '@amcharts/amcharts5/xy'
import { useEffect, useRef } from 'react'

import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel'
import { FilterSelect } from '#components/adminFilters/FilterSelect'
import { ReportDataWrapper } from '#components/ReportDataWrapper'
import { ReportFilterControls } from '#components/ReportFilterControls'
import { StatCard } from '#components/StatCard'
import { useFetch } from '#hooks/useAdminResource'
import {
  type BaseFilterValues,
  useReportFilters,
} from '#hooks/useReportFilters'

import {
  ProFinderCarrierBreakdown,
  type ProfinderCarrierStatItem,
} from './ProFinderCarrierBreakdown'

interface ProfinderStatsResult {
  totalRequests: number
  uniqueLoads: number
  prosFound: number
  uniqueProsFound: number
  byCarrier: ProfinderCarrierStatItem[]
}

interface DailyCarrierItem {
  date: string
  count: number
  byCarrier: { carrierCode: string; count: number }[]
}

interface ProfinderDailyResult {
  stepMinutes: number
  dailyByCarrier: DailyCarrierItem[]
}

const GOLDEN_RATIO = 0.618033988749895

const getSeriesColor = (index: number, total: number): am5.Color => {
  const hue = total <= 1 ? 0.39 : (index * GOLDEN_RATIO) % 1
  const saturation = index % 2 === 0 ? 0.75 : 0.55
  const lightness = index % 3 === 0 ? 0.45 : index % 3 === 1 ? 0.6 : 0.35
  return am5.Color.fromHSL(hue, saturation, lightness)
}

type ChartMetric = 'requests' | 'pros-found'
type ProfinderSource = 'freightview' | 'chr' | 'all'

interface ProfinderFilters extends BaseFilterValues {
  metric: ChartMetric
  source: ProfinderSource
}

type ChartDatum = Record<string, number> & { date: number; _total: number }

const getBaseInterval = (stepMinutes: number) => {
  const isHourly = stepMinutes < 1440
  if (!isHourly) return { timeUnit: 'day' as const, count: 1 }
  return stepMinutes < 60
    ? { timeUnit: 'minute' as const, count: stepMinutes }
    : { timeUnit: 'hour' as const, count: 1 }
}

const buildCarrierList = (dailyByCarrier: DailyCarrierItem[]): string[] => {
  const carriers = new Set<string>()
  dailyByCarrier.forEach(item => {
    item.byCarrier.forEach(c => carriers.add(c.carrierCode))
  })
  return Array.from(carriers).sort()
}

const buildProcessedData = (
  dailyByCarrier: DailyCarrierItem[],
  carrierList: string[],
): ChartDatum[] =>
  dailyByCarrier.map(item => {
    const [datePart, timePart] = item.date.split(' ')
    const [year, month, day] = datePart.split('-').map(Number)
    const [hour, minute] = timePart ? timePart.split(':').map(Number) : [0, 0]
    // Use local midnight so the DateAxis (which renders in local time) places bars on the correct calendar day
    const date = new Date(year, month - 1, day, hour, minute).getTime()
    const datum = { date, _total: 0 } as ChartDatum
    carrierList.forEach(c => {
      datum[c] = 0
    })
    let total = 0
    item.byCarrier.forEach(c => {
      datum[c.carrierCode] = c.count
      total += c.count
    })
    datum._total = total
    return datum
  })

const addCarrierSeries = (
  root: am5.Root,
  chart: am5xy.XYChart,
  xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
  yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
  carrierList: string[],
  processedData: ChartDatum[],
) => {
  carrierList.forEach((carrier, index) => {
    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: carrier.toUpperCase(),
        xAxis,
        yAxis,
        valueYField: carrier,
        valueXField: 'date',
        stacked: true,
      }),
    )
    const color = getSeriesColor(index, carrierList.length)
    series.columns.template.setAll({
      fill: color,
      stroke: color,
      tooltipText: '[bold]{name}[/]: {valueY}',
      tooltipY: 0,
    })
    series.data.setAll(processedData)
    series.appear()
  })
}

const attachLegendIsolation = (legend: am5.Legend, chart: am5xy.XYChart) => {
  let isolatedSeries: string | null = null

  const syncLegendStates = (activeName: string | null) => {
    legend.dataItems.forEach(dataItem => {
      const s = dataItem.dataContext as am5xy.ColumnSeries
      const name = s?.get('name')
      const isActive = activeName === null || name === activeName
      dataItem.get('itemContainer')?.set('disabled', !isActive)
    })
  }

  legend.itemContainers.template.setup = target => {
    target.events.on('click', async ev => {
      const nativeEvent = ev.originalEvent as MouseEvent
      if (!nativeEvent.metaKey && !nativeEvent.ctrlKey) return

      const clicked = target.dataItem?.dataContext as am5xy.ColumnSeries
      const clickedName = clicked?.get('name')
      if (!clickedName) return

      // Defer so the default legend toggle handler runs first
      await new Promise(resolve => setTimeout(resolve, 0))

      if (isolatedSeries === clickedName) {
        isolatedSeries = null
        chart.series.each(s => s.show())
        syncLegendStates(null)
      } else {
        isolatedSeries = clickedName
        chart.series.each(s => {
          if (s.get('name') === clickedName) s.show()
          else s.hide()
        })
        syncLegendStates(clickedName)
      }
    })
  }
}

const addXAxisDailyTotals = (
  xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
  processedData: ChartDatum[],
) => {
  // Key by local date string (YYYY-MM-DD) to match processedData's local-midnight timestamps
  const toLocalDateStr = (ms: number) => {
    const d = new Date(ms)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  const totalByIsoDate = new Map<string, number>(
    processedData.map(d => [toLocalDateStr(d.date), d._total]),
  )
  xAxis.get('renderer').labels.template.adapters.add('text', (text, target) => {
    const ms = (
      target.dataItem as unknown as { get(k: 'value'): number } | undefined
    )?.get('value')
    if (!ms) return text
    const total = totalByIsoDate.get(toLocalDateStr(ms))
    if (!total) return text
    return `${text ?? ''}\n[bold]${total.toLocaleString()}[/]`
  })
}

const ProFinderReport = () => {
  const { localFilters, setLocalFilters, apiFilters } =
    useReportFilters<ProfinderFilters>({
      defaultRange: '30d',
      initFromParams: params => ({
        metric: (params.get('metric') as ChartMetric) || 'pros-found',
        source: (params.get('source') as ProfinderSource) || 'chr',
      }),
      syncToParams: (params, filters) => {
        if (filters.metric !== 'pros-found')
          params.set('metric', filters.metric)
        else params.delete('metric')
        if (filters.source !== 'chr') params.set('source', filters.source)
        else params.delete('source')
      },
    })

  const metric = localFilters.metric
  const setMetric = (m: ChartMetric) =>
    setLocalFilters(prev => ({ ...prev, metric: m }))

  const source = localFilters.source
  const setSource = (s: ProfinderSource) =>
    setLocalFilters(prev => ({ ...prev, source: s }))

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refresh,
  } = useFetch<ProfinderStatsResult>('reports/profinder/stats', {
    ...apiFilters,
    source,
  })

  const { data: daily, isLoading: dailyLoading } =
    useFetch<ProfinderDailyResult>('reports/profinder/daily', {
      ...apiFilters,
      metric,
      source,
    })

  const chartContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!daily?.dailyByCarrier?.length || !chartContainerRef.current) return

    const root = am5.Root.new(chartContainerRef.current)
    root.setThemes([am5themes_Animated.new(root)])
    root.interfaceColors.set('text', am5.color(0xffffff))
    root.interfaceColors.set('grid', am5.color(0x444444))

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        layout: root.verticalLayout,
      }),
    )

    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, { behavior: 'none' }),
    )
    cursor.lineY.set('visible', false)

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: getBaseInterval(daily.stepMinutes),
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 50,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      }),
    )

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        min: 0,
      }),
    )

    const carrierList = buildCarrierList(daily.dailyByCarrier)
    const processedData = buildProcessedData(daily.dailyByCarrier, carrierList)

    addCarrierSeries(root, chart, xAxis, yAxis, carrierList, processedData)

    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50,
        marginTop: 15,
        marginBottom: 15,
      }),
    )
    attachLegendIsolation(legend, chart)
    legend.data.setAll(chart.series.values)

    addXAxisDailyTotals(xAxis, processedData)

    chart.appear(1000, 100)

    return () => {
      root.dispose()
    }
  }, [daily])

  const totals = stats
    ? {
        totalRequests: stats.totalRequests,
        uniqueLoads: stats.uniqueLoads,
        prosFound: stats.prosFound,
        uniqueProsFound: stats.uniqueProsFound,
      }
    : null

  return (
    <ReportDataWrapper
      isLoading={statsLoading}
      error={statsError}
      loadingMessage="Loading ProFinder stats..."
      onRetry={() => refresh()}
    >
      <div className="space-y-6 p-4">
        <AdminFilters isLoading={statsLoading} onRefresh={refresh}>
          <ReportFilterControls
            filters={localFilters}
            onChange={setLocalFilters}
          >
            <FilterSelect
              label="Source"
              value={source}
              onChange={v => setSource(v as ProfinderSource)}
              options={[
                { value: 'chr', label: 'CHR' },
                { value: 'freightview', label: 'Freightview' },
                { value: 'all', label: 'All' },
              ]}
            />
          </ReportFilterControls>
        </AdminFilters>

        {totals && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              title="Total Requests"
              value={totals.totalRequests.toLocaleString()}
            />
            <StatCard
              title="Unique Loads"
              value={totals.uniqueLoads.toLocaleString()}
            />
            <StatCard
              title="Pros Found"
              value={totals.prosFound.toLocaleString()}
              color="text-success"
            />
            <StatCard
              title="Unique Pros Found"
              value={totals.uniqueProsFound.toLocaleString()}
              color="text-success"
            />
          </div>
        )}

        {daily?.dailyByCarrier?.length || dailyLoading ? (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <h2 className="card-title">
                  {metric === 'requests' ? 'Requests' : 'Unique Pros Found'} per
                  Carrier
                </h2>
                <div className="join">
                  <button
                    type="button"
                    className={`join-item btn btn-sm ${metric === 'pros-found' ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setMetric('pros-found')}
                  >
                    Unique Pros Found
                  </button>
                  <button
                    type="button"
                    className={`join-item btn btn-sm ${metric === 'requests' ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setMetric('requests')}
                  >
                    Total Requests
                  </button>
                </div>
              </div>
              <div
                className="relative"
                style={{ width: '100%', height: '550px' }}
              >
                <div
                  ref={chartContainerRef}
                  style={{ width: '100%', height: '100%' }}
                />
                {dailyLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-base-100/60">
                    <span className="loading loading-spinner loading-lg" />
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {stats?.byCarrier?.length && totals ? (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body p-0">
              <h2 className="card-title p-4 pb-0">Carrier Breakdown</h2>
              <ProFinderCarrierBreakdown
                byCarrier={stats.byCarrier}
                totals={totals}
              />
            </div>
          </div>
        ) : null}

        {!stats && !statsLoading && !statsError && (
          <div className="alert">
            <span>No ProFinder data available for the selected range.</span>
          </div>
        )}
      </div>
    </ReportDataWrapper>
  )
}

export { ProFinderReport }
